export interface MeasureToolInstance {
    _started?: boolean;
    _geometry?: { nodes: unknown[] };
    _redrawOverlay?: () => void;
    _overlay?: google.maps.OverlayView;
}

export interface MeasureToolMapFallbackOptions {
    map: google.maps.Map;
    getMeasureTool: () => MeasureToolInstance | null;
    isEditPolygon: () => boolean;
    isDeleteMode: () => boolean;
    isOverPolyVertex: (event: google.maps.Data.MouseEvent) => boolean;
}

interface DomListenerRef {
    el: HTMLElement;
    type: string;
    handler: EventListener;
    capture: boolean;
}

export class MeasureToolMapFallback {
    private readonly map: google.maps.Map;
    private readonly mapDiv: HTMLElement;
    private readonly options: MeasureToolMapFallbackOptions;

    private projectionOverlay: google.maps.OverlayView | null = null;
    private mapListeners: google.maps.MapsEventListener[] = [];
    private domListeners: DomListenerRef[] = [];
    private attached = false;

    private lastMeasureTriggerKey = '';
    private lastMeasureClickAt = { x: 0, y: 0, time: 0 };
    private measureOverlayHiddenForZoom = false;
    private mapDragInProgress = false;
    private mapDragJustEnded = false;

    constructor(options: MeasureToolMapFallbackOptions) {
        this.options = options;
        this.map = options.map;
        this.mapDiv = options.map.getDiv();
    }

    isActive(): boolean {
        const measureTool = this.options.getMeasureTool();
        return !!(measureTool && measureTool._started);
    }

    getNodeCount(): number {
        const measureTool = this.options.getMeasureTool();
        const geometry = measureTool && measureTool._geometry;
        return geometry && geometry.nodes ? geometry.nodes.length : 0;
    }

    countMeasureOverlayNodes(): number {
        return this.mapDiv.querySelectorAll('[class*="measure-tool-measure-points"]').length;
    }

    cleanupStaleOverlays(): void {
        this.measureOverlayHiddenForZoom = false;
        this.mapDiv.querySelectorAll('[class*="measure-tool-measure-points"]').forEach((el) => {
            if (el.parentNode) {
                el.parentNode.removeChild(el);
            }
        });
    }

    attach(): void {
        if (this.attached) return;
        this.attached = true;

        this.projectionOverlay = new google.maps.OverlayView();
        this.projectionOverlay.onAdd = function() {};
        this.projectionOverlay.draw = function() {};
        this.projectionOverlay.setMap(this.map);

        this.addDomListener('contextmenu', this.onContextMenu, true);
        this.addDomListener('click', this.onClick, true);

        this.mapListeners.push(
            this.map.data.addListener('rightclick', (event: google.maps.Data.MouseEvent) => {
                this.onDataRightClick(event);
            })
        );

        this.mapListeners.push(
            this.map.addListener('dragstart', () => {
                this.mapDragInProgress = true;
                this.mapDragJustEnded = false;
            })
        );

        this.mapListeners.push(
            this.map.addListener('dragend', () => {
                this.mapDragInProgress = false;
                this.mapDragJustEnded = true;
            })
        );

        this.mapListeners.push(
            this.map.addListener('zoom_changed', () => {
                this.onZoomChanged();
            })
        );

        this.mapListeners.push(
            this.map.addListener('idle', () => {
                this.onIdle();
            })
        );
    }

    detach(): void {
        if (!this.attached) return;
        this.attached = false;

        this.mapListeners.forEach((listener) => listener.remove());
        this.mapListeners = [];

        this.domListeners.forEach(({ el, type, handler, capture }) => {
            el.removeEventListener(type, handler, capture);
        });
        this.domListeners = [];

        if (this.projectionOverlay) {
            this.projectionOverlay.setMap(null);
            this.projectionOverlay = null;
        }
    }

    private addDomListener(
        type: string,
        handler: EventListener,
        capture: boolean
    ): void {
        this.mapDiv.addEventListener(type, handler, capture);
        this.domListeners.push({ el: this.mapDiv, type, handler, capture });
    }

    private latLngFromMouseEvent(e: MouseEvent): google.maps.LatLng | null {
        const projection = this.projectionOverlay && this.projectionOverlay.getProjection();
        if (!projection) return null;
        const rect = this.mapDiv.getBoundingClientRect();
        const point = new google.maps.Point(e.clientX - rect.left, e.clientY - rect.top);
        return projection.fromContainerPixelToLatLng(point);
    }

    private triggerMeasureClick(
        latLng: google.maps.LatLng | null | undefined,
        domEvent: Event | null | undefined
    ): boolean {
        if (!this.isActive() || !latLng) return false;

        const mouseEvent = domEvent as MouseEvent | null;
        const now = Date.now();
        const x = mouseEvent ? mouseEvent.clientX : 0;
        const y = mouseEvent ? mouseEvent.clientY : 0;
        if (
            now - this.lastMeasureClickAt.time < 100 &&
            x === this.lastMeasureClickAt.x &&
            y === this.lastMeasureClickAt.y
        ) {
            return false;
        }
        this.lastMeasureClickAt = { x, y, time: now };

        google.maps.event.trigger(this.map, 'click', {
            latLng: latLng,
            domEvent: domEvent
        });
        return true;
    }

    private setMeasureOverlayVisible(visible: boolean): void {
        const display = visible ? 'visible' : 'hidden';
        this.mapDiv.querySelectorAll('[class*="measure-tool-measure-points"]').forEach((el) => {
            (el as HTMLElement).style.visibility = display;
        });
        this.measureOverlayHiddenForZoom = !visible;
    }

    private redrawMeasureToolOverlay(): void {
        if (!this.isActive()) return;
        const measureTool = this.options.getMeasureTool();
        if (!measureTool || typeof measureTool._redrawOverlay !== 'function') return;

        if (measureTool._overlay) {
            measureTool._overlay.setMap(null);
        }
        this.cleanupStaleOverlays();
        measureTool._redrawOverlay();
        if (measureTool._overlay) {
            measureTool._overlay.setMap(this.map);
        }
        this.setMeasureOverlayVisible(true);
    }

    private triggerMeasureRightClick(
        latLng: google.maps.LatLng | null | undefined,
        domEvent: Event | null | undefined,
        source: string
    ): boolean {
        if (this.options.isEditPolygon() || this.options.isDeleteMode() || !latLng) return false;

        const mouseEvent = domEvent as MouseEvent | null;
        const triggerKey = source + ':' + latLng.lat() + ',' + latLng.lng() + ':' +
            (mouseEvent ? mouseEvent.clientX + ',' + mouseEvent.clientY : '');
        if (triggerKey === this.lastMeasureTriggerKey) return false;
        this.lastMeasureTriggerKey = triggerKey;

        google.maps.event.trigger(this.map, 'rightclick', {
            latLng: latLng,
            domEvent: domEvent
        });
        return true;
    }

    private onContextMenu = (e: Event): void => {
        const mouseEvent = e as MouseEvent;
        if (this.options.isEditPolygon() || this.options.isDeleteMode()) return;

        const projection = this.projectionOverlay && this.projectionOverlay.getProjection();
        if (!projection) return;

        const rect = this.mapDiv.getBoundingClientRect();
        const point = new google.maps.Point(mouseEvent.clientX - rect.left, mouseEvent.clientY - rect.top);
        const latLng = projection.fromContainerPixelToLatLng(point);
        this.triggerMeasureRightClick(latLng, mouseEvent, 'contextmenu');
    };

    private onDataRightClick(event: google.maps.Data.MouseEvent): void {
        if (this.options.isEditPolygon() && this.options.isOverPolyVertex(event)) {
            return;
        }

        this.triggerMeasureRightClick(
            event.latLng,
            event.domEvent as Event,
            'data'
        );
    }

    private onClick = (e: Event): void => {
        if (!this.isActive()) return;

        const mouseEvent = e as MouseEvent;
        const nodeCountBefore = this.getNodeCount();
        const dragInProgress = this.mapDragInProgress;
        const dragJustEnded = this.mapDragJustEnded;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const nodeCountAfter = this.getNodeCount();

                if (nodeCountAfter <= nodeCountBefore && !dragInProgress && !dragJustEnded) {
                    const latLng = this.latLngFromMouseEvent(mouseEvent);
                    this.triggerMeasureClick(latLng, mouseEvent);
                }

                if (dragJustEnded) {
                    this.mapDragJustEnded = false;
                }
            });
        });
    };

    private onZoomChanged(): void {
        if (!this.isActive()) return;
        this.setMeasureOverlayVisible(false);
    }

    onMeasureEnd(): void {
        const measureTool = this.options.getMeasureTool();

        if (measureTool && measureTool._overlay) {
            measureTool._overlay.setMap(null);
        }
        this.cleanupStaleOverlays();
    }

    private onIdle(): void {
        if (!this.isActive()) {
            if (this.measureOverlayHiddenForZoom || this.countMeasureOverlayNodes() > 0) {
                this.cleanupStaleOverlays();
            }
            return;
        }
        if (!this.measureOverlayHiddenForZoom) return;
        this.redrawMeasureToolOverlay();
        this.measureOverlayHiddenForZoom = false;
    }
}
